import { useState } from 'react'

import { Button, Icon } from '@clientbase/clientbase-library'
import {
  Box,
  FormControlLabel,
  IconButton,
  Modal,
  Radio,
  RadioGroup,
  Stack,
  Typography,
  useMediaQuery
} from '@mui/material'
import { Plan, useAuthContext } from 'context/AuthContext'
import { useRouter } from 'next/router'

import { openSaasCheckout } from 'utils/saas-checkout-event'
import { settingsTabsParams } from 'utils/tabs'
import { track } from 'utils/tracking'

import { theme } from 'styles/theme'

import * as S from './ModalPricing.styles'

type ModalPricingProps = {
  handleCloseModal: () => void
  isOpen: boolean
  plan: Plan
  resourceName: string
}

type PlanToShow = {
  name: string
  price: number
  priceFirstMonths: number
  monthsCustomPricing: number
  description: string
  features: string[]
}

const ALL_PLANS_FEATURES_DEFAULT = [
  'PIX e boleto por R$1,99',
  'Emissão de Nota Fiscal automatizada',
  'Portal de Clientes personalizado'
]

const ALL_PLANS_FEATURES_NEW = [
  'PIX e boleto por R$1,70',
  'Emissão de Nota Fiscal automatizada',
  'Portal de Clientes personalizado'
]

const NEW_PLANS: ReadonlyArray<string> = ['start', 'grow', 'scale']

const LEGACY_TO_NEW_PLAN: Record<string, PlanKey> = {
  basic: 'start',
  pro: 'grow',
  ultra: 'scale'
}

type PlanKey = 'grow' | 'start' | 'scale'

const PLANS: { [key in PlanKey]: PlanToShow } = {
  start: {
    name: 'Plano Start',
    priceFirstMonths: 99,
    monthsCustomPricing: 3,
    price: 99,
    description:
      'Para empresas que estão começando e querem organizar sua operação recorrente.',
    features: [
      'Envio inteligente de notificações (Email, WhatsApp e SMS)',
      'Campos Extras no Link de Cadastro de Clientes',
      'Modelos de Contrato',
      'Edição de Mensagens na Régua de Cobrança',
      'Até 3 usuários, com níveis de acesso',
      'Gestão de Contas a Pagar'
    ]
  },
  grow: {
    name: 'Plano Grow',
    priceFirstMonths: 299,
    monthsCustomPricing: 3,
    price: 299,
    description:
      'Para empresas que querem uma gestão avançada e inteligente da sua operação recorrente.',
    features: [
      'Régua de cobrança personalizada',
      'Contrato gerado e enviado automaticamente no cadastro do cliente',
      'Aceite de Termos de Uso e Upload de Documentos no onboarding',
      'Até 10 usuários',
      'Módulo de Contas a Pagar e Receber Avançados',
      'Configurações Avançadas do Portal de Clientes e da Fatura',
      '1.000 comunicados por mês no Portal de Clientes (mensagens e arquivos)',
      'Métricas e dashboards avançados'
    ]
  },
  scale: {
    name: 'Plano Scale',
    priceFirstMonths: 499,
    monthsCustomPricing: 3,
    price: 499,
    description:
      'Para empresas que precisam de escala, automação avançada e suporte dedicado.',
    features: [
      'Tudo do Plano Grow',
      'Envio de Contratos em massa',
      'Régua de cobrança agressiva e desconto nas notificações extras',
      'Usuários ilimitados, com níveis de acesso expandidos',
      'API e integrações',
      'Suporte dedicado'
    ]
  }
}

const ModalPricing = ({
  isOpen,
  plan = 'start',
  handleCloseModal,
  resourceName
}: ModalPricingProps) => {
  const { push } = useRouter()
  const { modalPricing, setModalPricing } = useAuthContext()
  // Qual oferta originou este modal. Sem ela o clique chega ao Mixpanel com
  // `variant: undefined` e o teste A/B do paywall fica sem leitura.
  const variant = modalPricing?.variant ?? 'pricing'
  const match = useMediaQuery('(max-width: 600px)')
  const mappedPlan = LEGACY_TO_NEW_PLAN[plan as string] ?? plan
  const selectedPlan = (mappedPlan in PLANS ? mappedPlan : 'start') as PlanKey
  const isNewPlan = NEW_PLANS.includes(selectedPlan)
  const [planFrequency, setPlanFrequency] = useState<'yearly' | 'monthly'>(
    'monthly'
  )
  const allPlanFeatures = isNewPlan
    ? ALL_PLANS_FEATURES_NEW
    : ALL_PLANS_FEATURES_DEFAULT

  const CloseButton = () => (
    <IconButton
      sx={{
        alignSelf: 'end',
        position: !match ? 'relative' : 'absolute',
        top: !match ? '-12px' : '12px',
        right: !match ? '-12px' : '12px'
      }}
      onClick={handleCloseModal}
    >
      <Icon icon="close" />
    </IconButton>
  )

  return (
    <Modal aria-hidden={!isOpen} onClose={handleCloseModal} open={isOpen}>
      <S.Content size="extraLarge">
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: '8fr 4fr',
            height: '100%',

            [theme.breakpoints.down('sm')]: {
              gridTemplateColumns: '1fr'
            }
          }}
        >
          <Stack
            sx={{
              padding: !match ? '36px' : '36px 22px 22px 22px',
              height: !match ? '95%' : 'auto',
              gap: '32px',
              justifyContent: 'space-between'
            }}
          >
            <Stack
              direction={!match ? 'row' : 'column'}
              alignItems={!match ? 'flex-start' : 'center'}
              gap="18px"
            >
              {match && <CloseButton />}
              <Box
                sx={{
                  borderRadius: '999px',
                  backgroundColor: theme.palette.info[200],
                  width: '40px',
                  height: '40px',
                  minWidth: '40px',
                  minHeight: '40px',
                  display: 'grid',
                  placeItems: 'center'
                }}
              >
                <Icon
                  icon="arrowUpward"
                  sx={{ color: '#fff', fontSize: '30px' }}
                />
              </Box>
              <Stack textAlign={!match ? 'start' : 'center'}>
                <Typography variant="lgBold">
                  Desbloqueie mais recursos
                </Typography>
                <Typography>
                  O recurso {resourceName} não está disponível no seu plano
                  atual. Venda mais, receba sem esforço e cuide melhor dos seus
                  clientes com os recursos adicionais do{' '}
                  {PLANS[selectedPlan].name}.
                </Typography>
              </Stack>
            </Stack>

            <Stack
              sx={{
                border: '1px solid',
                borderColor: theme.palette.neutral[700],
                padding: '22px',
                borderRadius: '12px',
                gap: '15px',
                position: 'relative'
              }}
            >
              <Box
                sx={{
                  position: 'absolute',
                  top: '-16px',
                  borderRadius: '999px',
                  backgroundColor: theme.palette.info[200],
                  padding: '5px 20px'
                }}
              >
                <Typography variant="smBold" color="#fff">
                  Inclui {resourceName}
                </Typography>
              </Box>
              <Stack gap="20px">
                <Typography variant="lgBold">
                  {PLANS[selectedPlan].name}
                </Typography>
                <Typography variant="smBold">Recursos em destaque</Typography>
              </Stack>
              <Stack gap="16px">
                {PLANS[selectedPlan].features.map((resource: string) => (
                  <Stack
                    key={resource}
                    direction="row"
                    gap="5px"
                    alignItems="center"
                  >
                    <Box
                      sx={{
                        borderRadius: '999px',
                        border: '1px solid',
                        borderColor: theme.palette.info[200],
                        width: '16px',
                        height: '16px',
                        display: 'grid',
                        placeItems: 'center'
                      }}
                    >
                      <Icon
                        icon="arrowUpward"
                        sx={{
                          color: theme.palette.info[200],
                          fontSize: '14px'
                        }}
                      />
                    </Box>
                    <Typography>{resource}</Typography>
                  </Stack>
                ))}
              </Stack>
            </Stack>
          </Stack>

          <Stack
            sx={{
              padding: !match ? '36px' : '22px 22px 36px 22px',
              height: !match ? '95%' : 'auto',
              gap: !match ? '0' : '30px',
              backgroundColor: theme.palette.neutral[900],
              justifyContent: !match ? 'space-between' : 'flex-start'
            }}
          >
            <Stack gap="15px">
              {!match && <CloseButton />}

              <Stack gap="15px">
                {!isNewPlan && (
                  <RadioGroup
                    value={planFrequency}
                    onChange={(e) => setPlanFrequency(e.target.value as any)}
                    row
                  >
                    {' '}
                    <FormControlLabel
                      value="monthly"
                      control={
                        <Radio
                          sx={{
                            '&.Mui-checked': { color: theme.palette.info[200] }
                          }}
                        />
                      }
                      label={
                        <S.PlanFrequencyLabel
                          planFrequency={planFrequency}
                          value="monthly"
                        >
                          Mensal
                        </S.PlanFrequencyLabel>
                      }
                    />
                    <FormControlLabel
                      value="yearly"
                      control={
                        <Radio
                          sx={{
                            '&.Mui-checked': { color: theme.palette.info[200] }
                          }}
                        />
                      }
                      label={
                        <S.PlanFrequencyLabel
                          planFrequency={planFrequency}
                          value="yearly"
                          sx={{ position: 'relative', mr: '35px' }}
                        >
                          Anual <S.YearlyDiscountTag>-20%</S.YearlyDiscountTag>
                        </S.PlanFrequencyLabel>
                      }
                    />
                  </RadioGroup>
                )}

                <Stack justifyContent="space-between">
                  <Typography variant="lgBold">
                    {PLANS[selectedPlan].name}
                  </Typography>
                  {isNewPlan ? (
                    <Typography variant="lgBold" sx={{ fontSize: '28px' }}>
                      {`R$${PLANS[selectedPlan].price}/mês`}
                    </Typography>
                  ) : (
                    <>
                      <Typography>
                        {`R$${
                          planFrequency == 'monthly'
                            ? PLANS[selectedPlan].priceFirstMonths
                            : (PLANS[selectedPlan].priceFirstMonths * 0.8)
                                .toFixed(2)
                                .toString()
                                .replace('.', ',')
                        }/mês`}{' '}
                        nos {PLANS[selectedPlan].monthsCustomPricing} primeiros
                        meses
                      </Typography>
                      <Typography color={theme.palette.neutral[400]}>
                        {`R$${
                          planFrequency == 'monthly'
                            ? PLANS[selectedPlan].price
                            : (PLANS[selectedPlan].price * 0.8)
                                .toFixed(2)
                                .toString()
                                .replace('.', ',')
                        }/mês`}{' '}
                        a partir do quarto mês
                      </Typography>
                    </>
                  )}
                </Stack>

                <Typography>{PLANS[selectedPlan].description}</Typography>

                <Button
                  variantButton="pricing"
                  onClick={() => {
                    // SC-10694: última fronteira medível do funil de receita.
                    track('upgrade_cta_clicked', {
                      resource_name: resourceName,
                      selected_plan: selectedPlan,
                      plan_frequency: planFrequency,
                      destination: 'checkout',
                      variant
                    })

                    // SC-11070: a contratação acontece dentro do CONTA. O link
                    // externo criava o cadastro na conta de cobrança sem vínculo
                    // com esta conta.
                    handleCloseModal()
                    openSaasCheckout(selectedPlan, `paywall_${variant}`)
                  }}
                >
                  Alterar plano
                </Button>
              </Stack>
            </Stack>

            <Stack gap="22px">
              <Stack gap="10px">
                <Typography variant="smBold">
                  Incluso em todos os planos
                </Typography>

                <Stack gap="8px">
                  {allPlanFeatures.map((resource) => (
                    <Stack
                      key={resource}
                      direction="row"
                      gap="5px"
                      alignItems="center"
                    >
                      <Box
                        sx={{
                          borderRadius: '999px',
                          border: '1px solid',
                          borderColor: theme.palette.info[200],
                          width: '16px',
                          height: '16px',
                          display: 'grid',
                          placeItems: 'center'
                        }}
                      >
                        <Icon
                          icon="arrowForward"
                          sx={{
                            color: theme.palette.info[200],
                            fontSize: '14px'
                          }}
                        />
                      </Box>
                      <Typography>{resource}</Typography>
                    </Stack>
                  ))}
                </Stack>
              </Stack>

              <Button
                variantButton="outlined"
                sx={{ color: theme.palette.info[200] }}
                onClick={() => {
                  // Mesma intenção de upgrade por outra porta: separar as duas
                  // saídas mostra se as pessoas preferem comparar planos antes
                  // de ir ao checkout.
                  track('upgrade_cta_clicked', {
                    resource_name: resourceName,
                    selected_plan: selectedPlan,
                    plan_frequency: planFrequency,
                    destination: 'my_plan',
                    variant
                  })

                  push(`/settings?tab=${settingsTabsParams.MY_PLAN}`)
                  setModalPricing((prev) => ({ ...prev, isOpen: false }))
                }}
              >
                Ver todos os planos
              </Button>
            </Stack>
          </Stack>
        </Box>
      </S.Content>
    </Modal>
  )
}

export default ModalPricing
